import { useForm, SubmitHandler, SubmitErrorHandler } from "react-hook-form";
import { scrollToFirstError } from "@/utils/scrollToFirstError";
import { useState, useMemo, useEffect, useRef } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormSubmit } from "@/components/FormSubmit";
import { ArrowRightIcon } from "@/components/icons/icons";
import { api, AdvisorProfile } from "@/services/api";
import { useErrorHandler } from "@/context/ErrorHandlerContext";
import { useNavigate } from "react-router-dom";
import { useUpdateOnboardingStep } from "@/hooks/useUpdateOnboardingStep";
import { useExpertSignupPrefill } from "@/hooks/useExpertSignupPrefill";
import { MasterDataItem, useMasterData } from "@/hooks/useMasterData";
import { MASTER_SCOPES } from "@/constants/masterScopes";
import { parseMasterData } from "@/utils/util";
import { profileStepCompleted } from "@/utils/tracking";
import { useAuth } from "@/hooks/useAuth";
import { getPrimaryRole } from "@/utils/roleUtils";
import { SecondaryButton } from "@/components/buttonVariants";
import { CardCheckboxGroupField, CardCheckboxOption } from "@/components/CardCheckboxGroupField";
import {
  CONTRIBUTION_OPTIONS,
  PRIMARY_GOALS,
  PRIMARY_GOAL_ADVISE,
} from "@/constants/expertCapabilities";
import { MultiSelectFormField } from "@/components/MultiSelectFormField";
import { TextareaFormField } from "@/components/TextareaFormField";
import { YesNoField } from "@/components/YesNoField";

// The options are what the API calls them; the cards key on `name`.
const GOAL_OPTIONS: CardCheckboxOption[] = PRIMARY_GOALS.map(({ value, title }) => ({
  name: value,
  title,
}));

const advisorPreferenceObjectSchema = z.object({
  contributionGoals: z.array(z.string()).min(1, "Please select at least one primary goal"),
  joinPabBoard: z.boolean(),
  createLeadPrograms: z.boolean(),
  createLeadPeerPrograms: z.boolean(),
  createLeadOrganisationPrograms: z.boolean(),
  coreIndustries: z.array(z.string()).min(1, "Please select at least one core industry"),
  organisationTypes: z.array(z.string()).min(1, "Please select at least one organization type"),
  operatorThesis: z.string().trim().min(1, "Please share your operator thesis"),
  problemAndInsight: z.string().trim().min(1, "Please describe a problem you've solved"),
  // Only relevant (and required) when joinPabBoard is checked -- see the
  // superRefine below for the conditional requirement.
  multipleBoards: z.enum(["yes", "no"]).optional(),
  advisoryPhilosophy: z.string().trim().min(1, "Please describe your advisory philosophy"),
  engagementStructureRate: z
    .string()
    .trim()
    .min(1, "Please describe your preferred engagement structure and rate"),
});

const advisorPreferenceSchema = advisorPreferenceObjectSchema.superRefine((data, ctx) => {
  const anyContribution =
    data.joinPabBoard ||
    data.createLeadPrograms ||
    data.createLeadPeerPrograms ||
    data.createLeadOrganisationPrograms;
  if (!anyContribution) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please select at least one way you'd like to contribute",
      path: ["contributionTypes"],
    });
  }
  if (data.joinPabBoard && !data.multipleBoards) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Please select an option",
      path: ["multipleBoards"],
    });
  }
});

type AdvisorPreferenceFormInputs = z.infer<typeof advisorPreferenceSchema>;
type ContributionKey =
  | "joinPabBoard"
  | "createLeadPrograms"
  | "createLeadPeerPrograms"
  | "createLeadOrganisationPrograms";

interface AdvisorPreferenceFormProps {
  /**
   * When true, this is mounted inside the achiever-side Expert Application
   * wizard (an achiever applying to become an Expert, not yet holding the
   * advisor role) instead of the standalone advisor-signup flow. Reads/writes
   * go through the achiever-safe capabilities draft endpoint instead of the
   * advisor-only preference endpoints, and navigation/chrome is handed off
   * to the wizard instead of driven internally -- same pattern as
   * UserProfileForm's `embedded` prop.
   */
  embedded?: boolean;
  formId?: string;
  onSubmitSuccess?: () => void;
  onLoadingChange?: (loading: boolean) => void;
}

/**
 * Expert signup step 3 ("Operator DNA") and Expert Application step 3.
 *
 * Saves a draft only (PATCH /capabilities/me/draft), in both places: the
 * application isn't submitted until the S.T.A.R. Portfolio step after this
 * (StarPortfolioForm) is done.
 */
export const AdvisorPreferenceForm = ({
  embedded = false,
  formId,
  onSubmitSuccess,
  onLoadingChange,
}: AdvisorPreferenceFormProps) => {
  const [loading, setLoading] = useState(false);
  // v3: advisor identity is the user_id (SHA256 string), not an int PK.
  // Reused as the achiever's own id when embedded.
  const [advisorId, setAdvisorId] = useState<string | null>(null);
  const [existingProfile, setExistingProfile] = useState<
    AdvisorProfile | Record<string, unknown> | null
  >(null);
  const { user } = useAuth();
  const handleApiError = useErrorHandler();
  const navigate = useNavigate();
  const { updateStep } = useUpdateOnboardingStep();

  const {
    handleSubmit,
    formState: { errors, isSubmitted },
    setValue,
    getValues,
    trigger,
    watch,
    register,
  } = useForm<AdvisorPreferenceFormInputs>({
    resolver: zodResolver(advisorPreferenceSchema),
    // Only validate once the user has attempted to submit -- errors shouldn't
    // appear while they're still filling the form out for the first time.
    // Once they've submitted (and seen what's missing), re-validate live as
    // they fix each field.
    mode: "onSubmit",
    reValidateMode: "onChange",
    // scrollToFirstError moves to the first error in page order instead —
    // RHF's own focus only reaches the four register()ed textareas and would
    // skip past an empty multi-select above them.
    shouldFocusError: false,
    defaultValues: {
      contributionGoals: [],
      joinPabBoard: false,
      createLeadPrograms: false,
      createLeadPeerPrograms: false,
      createLeadOrganisationPrograms: false,
      coreIndustries: [],
      organisationTypes: [],
      operatorThesis: "",
      problemAndInsight: "",
      multipleBoards: undefined,
      advisoryPhilosophy: "",
      engagementStructureRate: "",
    },
  });

  const formRef = useRef<HTMLFormElement>(null);

  // Prefill from the Expert's CRM record, once their saved answers have
  // loaded, and only into fields still empty (see UserProfileForm).
  const [hydrated, setHydrated] = useState(false);
  const [crmPrefilled, setCrmPrefilled] = useState(false);
  const crmAppliedRef = useRef(false);
  const { data: crmPrefill } = useExpertSignupPrefill(true);

  useEffect(() => {
    if (!hydrated || !crmPrefill || crmAppliedRef.current) return;
    crmAppliedRef.current = true;

    const fillList = (
      field: "contributionGoals" | "coreIndustries" | "organisationTypes",
      values: string[],
    ) => {
      if (values.length === 0 || (getValues(field) ?? []).length > 0) return false;
      setValue(field, values, { shouldDirty: true });
      return true;
    };
    const fillText = (
      field: "operatorThesis" | "problemAndInsight" | "advisoryPhilosophy",
      value: string | null,
    ) => {
      if (!value || getValues(field)) return false;
      setValue(field, value, { shouldDirty: true });
      return true;
    };

    const goalsFilled = fillList("contributionGoals", crmPrefill.contributionGoals);
    // Same rule as ticking it by hand: advising means a PAB seat.
    if (goalsFilled && crmPrefill.contributionGoals.includes(PRIMARY_GOAL_ADVISE)) {
      setValue("joinPabBoard", true, { shouldDirty: true });
    }
    const filled = [
      goalsFilled,
      fillList("coreIndustries", crmPrefill.coreIndustries),
      fillList("organisationTypes", crmPrefill.organisationTypes),
      fillText("operatorThesis", crmPrefill.operatorThesis),
      fillText("problemAndInsight", crmPrefill.problemAndInsight),
      fillText("advisoryPhilosophy", crmPrefill.advisoryPhilosophy),
    ].some(Boolean);
    setCrmPrefilled(filled);
  }, [hydrated, crmPrefill, getValues, setValue]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  useEffect(() => {
    const hydrateFromAdvisor = async () => {
      try {
        const advisor = await api.advisors.getAdvisor();
        setAdvisorId(advisor.userId);

        try {
          const profile = await api.advisors.getProfile(advisor.userId);
          if (profile) {
            setExistingProfile(profile);
            if (Array.isArray(profile.organisationType) && profile.organisationType.length > 0) {
              setValue("organisationTypes", profile.organisationType);
            }
          }
        } catch {
          // No profile yet — step 2 wasn't completed, nothing to hydrate.
        }

        await hydrateCapabilities();
      } catch {
        // Silently ignore — form starts with empty defaults
      } finally {
        setHydrated(true);
      }
    };

    const hydrateFromAchiever = async () => {
      try {
        const achiever = await api.achievers.getAchieverMe();
        setAdvisorId(achiever.id);

        try {
          const profile = (await api.achievers.getProfile(achiever.id)) as Record<string, unknown>;
          if (profile) {
            setExistingProfile(profile);
            if (Array.isArray(profile.organisationType) && profile.organisationType.length > 0) {
              setValue("organisationTypes", profile.organisationType as string[]);
            }
          }
        } catch {
          // No profile yet — nothing to hydrate.
        }

        await hydrateCapabilities();
      } catch {
        // Silently ignore — form starts with empty defaults
      } finally {
        setHydrated(true);
      }
    };

    // Both flows save this step as a capabilities draft, so both read it back
    // from there (an Expert has no advisor preference until step 4).
    const hydrateCapabilities = async () => {
      try {
        const capabilities = (await api.capabilities.getMine()) as unknown as Record<
          string,
          unknown
        >;
        if (capabilities) hydratePreferenceFields(capabilities);
      } catch {
        // No capabilities row yet — first time, this step creates it.
      }
    };

    const hydratePreferenceFields = (pref: Record<string, unknown>) => {
      if (Array.isArray(pref.contributionGoals)) {
        // Only goals the form still offers -- an unknown value couldn't be
        // unticked, and would be re-saved forever.
        const known = PRIMARY_GOALS.map((goal) => goal.value);
        setValue(
          "contributionGoals",
          (pref.contributionGoals as string[]).filter((goal) => known.includes(goal)),
        );
      }
      for (const { name } of CONTRIBUTION_OPTIONS) {
        const saved = pref[name];
        if (typeof saved === "boolean") setValue(name as ContributionKey, saved);
      }
      if (Array.isArray(pref.coreIndustries) && pref.coreIndustries.length > 0) {
        setValue("coreIndustries", pref.coreIndustries as string[]);
      }
      if (typeof pref.operatorThesis === "string" && pref.operatorThesis) {
        setValue("operatorThesis", pref.operatorThesis);
      }
      if (typeof pref.problemAndInsight === "string" && pref.problemAndInsight) {
        setValue("problemAndInsight", pref.problemAndInsight);
      }
      if (typeof pref.advisoryPhilosophy === "string" && pref.advisoryPhilosophy) {
        setValue("advisoryPhilosophy", pref.advisoryPhilosophy);
      }
      if (typeof pref.engagementStructure === "string" && pref.engagementStructure) {
        setValue("engagementStructureRate", pref.engagementStructure);
      }
      if (
        pref.interestMultiAdvisorBoards !== undefined &&
        pref.interestMultiAdvisorBoards !== null
      ) {
        setValue("multipleBoards", pref.interestMultiAdvisorBoards ? "yes" : "no");
      }
    };

    if (embedded) {
      hydrateFromAchiever();
    } else {
      hydrateFromAdvisor();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, setValue]);

  const contributionValues: Record<ContributionKey, boolean> = {
    joinPabBoard: watch("joinPabBoard"),
    createLeadPrograms: watch("createLeadPrograms"),
    createLeadPeerPrograms: watch("createLeadPeerPrograms"),
    createLeadOrganisationPrograms: watch("createLeadOrganisationPrograms"),
  };
  const contributionGoals = watch("contributionGoals") || [];
  const goalValues = Object.fromEntries(
    PRIMARY_GOALS.map(({ value }) => [value, contributionGoals.includes(value)]),
  );
  const coreIndustries = watch("coreIndustries") || [];
  const organisationTypes = watch("organisationTypes") || [];
  const multipleBoards = watch("multipleBoards");

  const { data: industryData } = useMasterData<MasterDataItem>(MASTER_SCOPES.INDUSTRY);
  const { data: organisationTypeData } = useMasterData<MasterDataItem>(
    MASTER_SCOPES.ORGANISATION_TYPE,
  );
  const industryOptions = useMemo(() => parseMasterData(industryData) || [], [industryData]);
  const organisationTypeOptions = useMemo(
    () => parseMasterData(organisationTypeData) || [],
    [organisationTypeData],
  );

  const toggleGoal = (goal: string) => {
    const adding = !contributionGoals.includes(goal);
    setValue(
      "contributionGoals",
      adding ? [...contributionGoals, goal] : contributionGoals.filter((g) => g !== goal),
    );
    // Advising is what a Personal Advisory Board seat is, so choosing that
    // goal ticks the PAB option for them. Deselecting it leaves the PAB choice
    // alone -- they may still want it.
    if (adding && goal === PRIMARY_GOAL_ADVISE) setValue("joinPabBoard", true);
    if (isSubmitted) trigger();
  };

  const toggleContribution = (name: string) => {
    const key = name as ContributionKey;
    const nextValue = !contributionValues[key];
    setValue(key, nextValue);
    // Multiple-PABs only applies once "Join Personal Advisory Boards" is
    // checked -- clear any stale answer when it's unchecked, so a leftover
    // yes/no from an earlier toggle isn't silently submitted.
    if (key === "joinPabBoard" && !nextValue) {
      setValue("multipleBoards", undefined);
    }
    if (isSubmitted) trigger();
  };

  // Take the user to the first problem on the page.
  const onInvalid: SubmitErrorHandler<AdvisorPreferenceFormInputs> = () => {
    scrollToFirstError(formRef.current);
  };

  const onsubmit: SubmitHandler<AdvisorPreferenceFormInputs> = async (data) => {
    setLoading(true);
    try {
      // Organisation Types belongs to the user's own profile (step 2), not capabilities.
      if (embedded) {
        const id = advisorId ?? (await api.achievers.getAchieverMe()).id;
        if (existingProfile) {
          await api.achievers.updateProfile(id, {
            ...existingProfile,
            organisationType: data.organisationTypes,
          });
        }
      } else {
        const id = advisorId ?? (await api.advisors.getAdvisor()).userId;
        if (existingProfile) {
          const advisorProfile = existingProfile as AdvisorProfile;
          await api.advisors.updateProfile(id, {
            currentTitle: advisorProfile.currentTitle,
            currentCompany: advisorProfile.currentCompany,
            location: advisorProfile.location,
            yearsOfExperience: advisorProfile.yearsOfExperience,
            primaryFunction: advisorProfile.primaryFunction,
            currentRoleLevel: advisorProfile.currentRoleLevel,
            topGrowthAreas: advisorProfile.topGrowthAreas,
            experienceContext: advisorProfile.experienceContext,
            jobRole: advisorProfile.jobRole,
            industry: advisorProfile.industry,
            bio: advisorProfile.bio,
            organisationType: data.organisationTypes,
          });
        }
      }

      // A draft, not a submission -- the S.T.A.R. Portfolio step still has to
      // be done, and that step is what submits the application.
      // joinAsCoExpert / peerAdvisory / coFounding are no longer asked, so
      // they're not sent: an existing Expert's earlier answers stay as they were.
      await api.capabilities.saveDraft({
        contributionGoals: data.contributionGoals,
        joinPabBoard: data.joinPabBoard,
        createLeadPrograms: data.createLeadPrograms,
        createLeadPeerPrograms: data.createLeadPeerPrograms,
        createLeadOrganisationPrograms: data.createLeadOrganisationPrograms,
        coreIndustries: data.coreIndustries,
        operatorThesis: data.operatorThesis,
        problemAndInsight: data.problemAndInsight,
        advisoryPhilosophy: data.advisoryPhilosophy,
        engagementStructure: data.engagementStructureRate,
        interestMultiAdvisorBoards: data.multipleBoards === "yes",
      });

      if (embedded) {
        onSubmitSuccess?.();
        return;
      }

      if (user) {
        profileStepCompleted({
          step_name: "preferences_completed",
          user_role: getPrimaryRole(user),
        });
      }

      await updateStep("signup/4");
      navigate("/signup/4");
    } catch (err: unknown) {
      handleApiError(err, advisorPreferenceObjectSchema);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      ref={formRef}
      id={formId}
      onSubmit={handleSubmit(onsubmit, onInvalid)}
      className="flex flex-col gap-6 font-inter"
    >
      {/* Heading */}
      {!embedded && (
        <div className="flex flex-col gap-2">
          <h1 className="text-[28px] font-bold leading-tight tracking-[0.3px] text-theme-text-heading">
            Expert Contribution
          </h1>
          <p className="text-18 text-preference-card-subtitle leading-7 tracking-[-0.44px]">
            Tell us how you want to contribute as an Expert
          </p>
        </div>
      )}

      {crmPrefilled && (
        <p className="rounded-[10px] border border-theme-border bg-theme-bg-surface px-4 py-3 text-14 text-theme-text-secondary">
          We&apos;ve pre-filled some answers from your Expert application. Please review them
          before continuing.
        </p>
      )}

      {/* Field 1: Primary Goal for Joining */}
      <CardCheckboxGroupField
        label="Primary Goal For Joining"
        hint="(Multi-select)"
        required
        options={GOAL_OPTIONS}
        values={goalValues}
        onToggle={toggleGoal}
        error={errors.contributionGoals?.message}
      />

      {/* Field 2: Contribution type */}
      <CardCheckboxGroupField
        label="How do you want to contribute as an Expert?"
        hint="(Multi-select)"
        required
        options={CONTRIBUTION_OPTIONS}
        values={contributionValues}
        onToggle={toggleContribution}
        error={(errors as Record<string, { message?: string }>).contributionTypes?.message}
      />

      {/* Field 3: Core Industries */}
      <MultiSelectFormField
        id="coreIndustries"
        label="Core Industries"
        description="Select all that apply"
        required
        options={industryOptions}
        selected={coreIndustries}
        onChange={(selected) => {
          setValue("coreIndustries", selected);
          if (isSubmitted) trigger("coreIndustries");
        }}
        placeholder="Select core industries..."
        itemLabel="industry"
        error={errors.coreIndustries?.message}
      />

      {/* Field 4: Organisation Types */}
      <MultiSelectFormField
        id="organisationTypes"
        label="Organization Types"
        description="Select all that apply"
        required
        options={organisationTypeOptions}
        selected={organisationTypes}
        onChange={(selected) => {
          setValue("organisationTypes", selected);
          if (isSubmitted) trigger("organisationTypes");
        }}
        placeholder="Select organization types..."
        itemLabel="type"
        error={errors.organisationTypes?.message}
      />

      {/* The four text answers pass their watched value as well as register():
          they are filled after mount (saved draft, CRM prefill) by setValue,
          which fires no input event, and the Textarea only re-measures its
          height when `value` changes -- without it, filled text was cut off. */}

      {/* Field 5: Operator Thesis */}
      <TextareaFormField
        id="operatorThesis"
        richText
        label="Operator Thesis"
        required
        description="What is your core operating philosophy? What do you believe about how companies grow, how teams should work, or how leaders should lead? (3–5 sentences)"
        placeholder="e.g. I believe the best operators build for outcomes, not outputs. My thesis is that sustainable growth comes from…"
        rows={4}
        {...register("operatorThesis")}
        value={watch("operatorThesis")}
        error={errors.operatorThesis?.message}
      />

      {/* Field 6: Problem & Insight */}
      <TextareaFormField
        id="problemAndInsight"
        richText
        label="Problem & Insight"
        required
        description="What is a specific problem you have solved in your career that most people have not? What was your insight, and what did you learn?"
        placeholder="Describe a challenge you uniquely solved, the insight that led you there, and what you learned…"
        rows={4}
        {...register("problemAndInsight")}
        value={watch("problemAndInsight")}
        error={errors.problemAndInsight?.message}
      />

      {/* Field 7: Multiple PABs -- only relevant once "Join Personal Advisory Boards" is checked */}
      {contributionValues.joinPabBoard && (
        <YesNoField
          label="Are you interested in participating in more than one Personal Advisory Board?"
          required
          value={multipleBoards}
          onChange={(value) => {
            setValue("multipleBoards", value);
            if (isSubmitted) trigger("multipleBoards");
          }}
          error={errors.multipleBoards?.message}
        />
      )}

      {/* Field 8: Advisory Philosophy */}
      <TextareaFormField
        id="advisoryPhilosophy"
        richText
        label="Advisory Philosophy"
        required
        description="How do you show up as an expert, fractional, or co-founder? What is your approach to working with founders and teams?"
        placeholder="e.g. I lead with questions before recommendations. I believe the best advisory relationships are built on trust, candour, and a clear focus on outcomes…"
        rows={4}
        {...register("advisoryPhilosophy")}
        value={watch("advisoryPhilosophy")}
        error={errors.advisoryPhilosophy?.message}
      />

      {/* Field 9: Engagement Structure & Rate */}
      <TextareaFormField
        id="engagementStructureRate"
        richText
        label="Engagement Structure & Rate"
        required
        labelHint="This is not going to be publicly available"
        description="How do you prefer to structure engagements (e.g. retainer, project-based, equity, advisory)? What is your typical rate or range?"
        placeholder="e.g. I prefer retainer-based arrangements of 8–16 hrs/month. My advisory rate is $X–Y/hr or $Z/month. Open to equity for early-stage…"
        rows={4}
        {...register("engagementStructureRate")}
        value={watch("engagementStructureRate")}
        error={errors.engagementStructureRate?.message}
      />

      {/* Buttons */}
      {!embedded && (
        <div className="flex items-stretch gap-4 pt-2 h-12 md:h-[58px]">
          <SecondaryButton
            className="text-14 md:text-16 text-back-btn-text text-center font-inter font-semibold"
            onClick={() => navigate("/signup/2")}
          >
            Back
          </SecondaryButton>
          <div className="flex-1">
            <FormSubmit
              isLoading={loading}
              disabled={loading}
              variant="primaryGradient"
              className="h-14 text-14 md:text-16"
              rightIcon={<ArrowRightIcon className="text-white" />}
            >
              Continue to STAR Portfolio
            </FormSubmit>
          </div>
        </div>
      )}
    </form>
  );
};
